import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  Menu,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { MoreVert as MoreVertIcon } from '@mui/icons-material'
import {
  SOCIAL_PUBLICATION_STATUSES,
  socialApi,
  type SocialCancelResponse,
  type SocialPaginationMeta,
  type SocialPublication,
  type SocialPublicationStatus,
} from '@/api/social'
import { SocialCancelDialog, SocialCancelResultAlert } from '@/components/admin/social/SocialCancelDialog'
import { SocialPublicationDialog } from '@/components/admin/social/SocialPublicationDialog'
import { SocialItemStatusChip, SocialPublicationStatusChip } from '@/components/admin/social/SocialStatusChips'
import { canCancelPublication, targetLabel } from '@/components/admin/social/socialUi'
import { formatDateTimeDMY } from '@/utils/date'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { safeHttpUrl } from '@/utils/url'

type StatusFilter = SocialPublicationStatus | 'all'

const EMPTY_META: SocialPaginationMeta = { current_page: 1, last_page: 1, per_page: 10, total: 0 }

/** Вкладка «Журнал»: все публикации со статусами шагов, детали и решения по шагам */
export function SocialJournalTab() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [rows, setRows] = useState<SocialPublication[]>([])
  const [meta, setMeta] = useState<SocialPaginationMeta>(EMPTY_META)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [status, setStatus] = useState<StatusFilter>('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cancelResult, setCancelResult] = useState<SocialCancelResponse | null>(null)

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [menuPublication, setMenuPublication] = useState<SocialPublication | null>(null)

  const [detailsOpen, setDetailsOpen] = useState(false)
  const [detailsId, setDetailsId] = useState<number | null>(null)

  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelTarget, setCancelTarget] = useState<SocialPublication | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await socialApi.getPublications({
        status: status === 'all' ? undefined : status,
        page: page + 1,
        per_page: rowsPerPage,
      })
      setRows(result.data)
      setMeta(result.meta)
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('admin.social.loadFailed')))
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, status, t])

  useEffect(() => {
    void load()
  }, [load])

  const openMenu = (event: React.MouseEvent<HTMLElement>, publication: SocialPublication) => {
    setMenuAnchor(event.currentTarget)
    setMenuPublication(publication)
  }

  const newsCell = (publication: SocialPublication) => {
    const url = safeHttpUrl(publication.news?.url)
    const title = publication.news?.title ?? t('admin.social.newsMissing', { id: publication.news_id })

    return (
      <Typography fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
        {url ? (
          <Link href={url} target="_blank" rel="noopener noreferrer" underline="hover" color="inherit">
            {title}
          </Link>
        ) : (
          title
        )}
      </Typography>
    )
  }

  const itemChips = (publication: SocialPublication) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {publication.items.map((item) => (
        <SocialItemStatusChip
          key={item.id}
          status={item.status}
          label={targetLabel(item.target, item.social_target_id)}
        />
      ))}
    </Box>
  )

  const tableHead = (
    <TableHead>
      <TableRow>
        <TableCell>#</TableCell>
        <TableCell>{t('admin.social.journal.columnNews')}</TableCell>
        <TableCell>{t('admin.social.journal.columnCreated')}</TableCell>
        <TableCell>{t('admin.social.journal.columnStatus')}</TableCell>
        <TableCell>{t('admin.social.journal.columnItems')}</TableCell>
        <TableCell align="right">{t('admin.users.actions')}</TableCell>
      </TableRow>
    </TableHead>
  )

  return (
    <Box>
      {cancelResult && <SocialCancelResultAlert result={cancelResult} onClose={() => setCancelResult(null)} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
          <InputLabel id="social-journal-status">{t('admin.social.journal.filterStatus')}</InputLabel>
          <Select
            labelId="social-journal-status"
            label={t('admin.social.journal.filterStatus')}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as StatusFilter)
              setPage(0)
            }}
          >
            <MenuItem value="all">{t('admin.social.journal.all')}</MenuItem>
            {SOCIAL_PUBLICATION_STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`admin.social.publicationStatus.${value}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[1, 2, 3].map((i) => (
              <Paper key={i} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Skeleton variant="text" width={40} height={20} />
                  <Skeleton variant="circular" width={32} height={32} />
                </Box>
                <Skeleton variant="text" width="70%" height={24} />
                <Skeleton variant="text" width="40%" height={20} />
                <Skeleton variant="rectangular" width={160} height={24} sx={{ borderRadius: 1, mt: 1 }} />
              </Paper>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              {tableHead}
              <TableBody>
                {[1, 2, 3, 4, 5].map((i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton variant="text" width={24} /></TableCell>
                    <TableCell><Skeleton variant="text" width={220} /></TableCell>
                    <TableCell><Skeleton variant="text" width={110} /></TableCell>
                    <TableCell><Skeleton variant="rectangular" width={100} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                    <TableCell><Skeleton variant="rectangular" width={220} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                    <TableCell align="right"><Skeleton variant="circular" width={32} height={32} sx={{ ml: 'auto' }} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : meta.total === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">{t('admin.social.journal.empty')}</Typography>
        </Paper>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {rows.map((publication) => (
            <Paper key={publication.id} sx={{ p: 2, minWidth: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  #{publication.id}
                </Typography>
                <IconButton
                  size="small"
                  aria-label={t('admin.users.actions')}
                  onClick={(event) => openMenu(event, publication)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
              {newsCell(publication)}
              <Typography variant="caption" color="text.secondary">
                {formatDateTimeDMY(publication.created_at)}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <SocialPublicationStatusChip status={publication.status} />
              </Box>
              <Box sx={{ mt: 1 }}>{itemChips(publication)}</Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            {tableHead}
            <TableBody>
              {rows.map((publication) => (
                <TableRow key={publication.id}>
                  <TableCell>{publication.id}</TableCell>
                  <TableCell>{newsCell(publication)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTimeDMY(publication.created_at)}</TableCell>
                  <TableCell>
                    <SocialPublicationStatusChip status={publication.status} />
                  </TableCell>
                  <TableCell>{itemChips(publication)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label={t('admin.users.actions')}
                      onClick={(event) => openMenu(event, publication)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && meta.total > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, overflow: 'hidden', minWidth: 0 }}>
          <TablePagination
            component="div"
            count={meta.total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 15, 25, 50]}
            labelRowsPerPage={isMobile ? '' : t('admin.social.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => t('admin.social.displayedRows', { from, to, count })}
          />
        </Box>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        TransitionProps={{ onExited: () => setMenuPublication(null) }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            if (menuPublication) {
              setDetailsId(menuPublication.id)
              setDetailsOpen(true)
            }
            setMenuAnchor(null)
          }}
        >
          {t('admin.social.journal.details')}
        </MenuItem>
        {menuPublication && canCancelPublication(menuPublication) && (
          <MenuItem
            onClick={() => {
              setCancelTarget(menuPublication)
              setCancelOpen(true)
              setMenuAnchor(null)
            }}
          >
            {t('admin.social.journal.cancel')}
          </MenuItem>
        )}
      </Menu>

      <SocialPublicationDialog
        open={detailsOpen}
        publicationId={detailsId}
        onClose={() => setDetailsOpen(false)}
        onChanged={() => void load()}
      />

      <SocialCancelDialog
        open={cancelOpen}
        publication={cancelTarget}
        onClose={() => setCancelOpen(false)}
        onCancelled={(response) => {
          setCancelOpen(false)
          setError(null)
          setCancelResult(response)
          void load()
        }}
      />
    </Box>
  )
}
